/**
 * Recipe Cost Hook
 *
 * User Story: US-P6-002 - Recipe Cost Integration
 * Steps 7 & 9: Recipe Cost Calculation & Version History
 *
 * Provides hooks for:
 * - Calculate recipe cost using FIFO preview
 * - Get actual cost for completed production batches
 * - List recipes with cost summaries
 * - Get version history for recipes
 * - Create new recipe versions
 */

import { useState, useCallback, useEffect } from 'react'
import { supabase } from '@/supabase-client'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY
const FUNCTION_URL = `${SUPABASE_URL}/functions/v1/recipes`

/**
 * Call the recipes edge function with authenticated session
 */
async function callRecipesFunction(action, data = {}) {
  // Get the user's session token for authenticated access
  const session = await supabase.auth.getSession()
  const accessToken = session?.data?.session?.access_token

  if (!accessToken) {
    throw new Error('Authentication required. Please log in.')
  }

  const response = await fetch(FUNCTION_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
      'apikey': SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...data }),
  })

  const result = await response.json()

  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }

  return result
}

/**
 * Hook for calculating recipe cost
 *
 * Provides:
 * - recipeCost: Current cost breakdown with FIFO estimates
 * - isLoading: Loading state
 * - error: Error state
 * - calculateCost: Trigger cost calculation for a recipe
 * - getActualCost: Get actual cost for a production batch
 * - refetch: Refresh the calculation
 *
 * @param {string} recipeId - Optional recipe ID to auto-fetch on mount
 * @returns {Object} - Hook state and methods
 */
export function useRecipeCost(recipeId = null) {
  const [recipeCost, setRecipeCost] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Calculate recipe cost using FIFO preview and ratio-based calculation
   *
   * Uses the formula:
   *   total_batch_kg = total_blocks × block_weight_kg
   *   total_parts = SUM(ingredient.parts)
   *   ingredient_kg = (ingredient.parts / total_parts) × total_batch_kg
   *
   * @param {string} id - Recipe ID
   * @param {number} totalBlocks - Number of blocks to produce (default: 54)
   * @param {number} blockWeightKg - Weight per block in kg (default: 4.5)
   * @returns {Promise<Object>} - Cost breakdown with calculated quantities
   */
  const calculateCost = useCallback(async (id, totalBlocks = 54, blockWeightKg = 4.5) => {
    if (!id) {
      setError('Recipe ID is required')
      return null
    }

    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('cost', {
        id,
        total_blocks: totalBlocks,
        block_weight_kg: blockWeightKg
      })
      setRecipeCost(result.data)
      return result.data
    } catch (err) {
      console.error('Error calculating recipe cost:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Get actual production cost from usage records
   * This returns the real cost after materials have been consumed
   *
   * @param {string} substrateBatchId - Production batch ID
   * @returns {Promise<Object>} - Actual cost with material breakdown
   */
  const getActualCost = useCallback(async (substrateBatchId) => {
    if (!substrateBatchId) {
      throw new Error('Substrate batch ID is required')
    }

    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('actual-cost', {
        substrate_batch_id: substrateBatchId
      })

      return result.data
    } catch (err) {
      console.error('Error getting actual cost:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Refetch cost for the current recipe
   */
  const refetch = useCallback(async () => {
    if (recipeId) {
      return calculateCost(recipeId)
    }
    return null
  }, [recipeId, calculateCost])

  /**
   * Clear error state
   */
  const clearError = useCallback(() => setError(null), [])

  // Auto-fetch on mount if recipeId provided
  useEffect(() => {
    if (recipeId) {
      calculateCost(recipeId)
    }
  }, [recipeId, calculateCost])

  return {
    // State
    recipeCost,
    isLoading,
    error,

    // Methods
    calculateCost,
    getActualCost,
    refetch,
    clearError
  }
}

/**
 * Hook for fetching a recipe with cost data
 *
 * @param {string} recipeId - Recipe ID
 * @param {Object} options - Options
 * @param {boolean} options.includeCost - Include cost calculation (default: true)
 * @returns {Object} - Recipe data and methods
 */
export function useRecipeWithCost(recipeId, options = {}) {
  const { includeCost = true } = options
  const [recipe, setRecipe] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Fetch recipe with optional cost
   */
  const fetchRecipe = useCallback(async (id) => {
    if (!id) {
      setError('Recipe ID is required')
      return null
    }

    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('get', {
        id,
        include_cost: includeCost
      })

      setRecipe(result.data)
      return result.data
    } catch (err) {
      console.error('Error fetching recipe:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [includeCost])

  /**
   * Refetch the current recipe
   */
  const refetch = useCallback(() => {
    if (recipeId) {
      return fetchRecipe(recipeId)
    }
    return null
  }, [recipeId, fetchRecipe])

  // Auto-fetch on mount
  useEffect(() => {
    if (recipeId) {
      fetchRecipe(recipeId)
    }
  }, [recipeId, fetchRecipe])

  return {
    recipe,
    isLoading,
    error,
    refetch,
    clearError: useCallback(() => setError(null), [])
  }
}

/**
 * Hook for listing recipes with optional cost data
 *
 * @param {Object} options - Options
 * @param {boolean} options.includeCost - Include cost calculation (default: false)
 * @param {boolean} options.activeOnly - Only active recipes (default: true)
 * @param {boolean} options.currentOnly - Only current versions (default: true)
 * @returns {Object} - Recipes list and methods
 */
export function useRecipesWithCost(options = {}) {
  const {
    includeCost = false,
    activeOnly = true,
    currentOnly = true
  } = options

  const [recipes, setRecipes] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Fetch all recipes
   */
  const fetchRecipes = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('list', {
        include_cost: includeCost,
        active_only: activeOnly,
        current_only: currentOnly
      })

      setRecipes(result.data || [])
      return result.data
    } catch (err) {
      console.error('Error fetching recipes:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [includeCost, activeOnly, currentOnly])

  /**
   * Refetch recipes
   */
  const refetch = useCallback(() => fetchRecipes(), [fetchRecipes])

  // Auto-fetch on mount
  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  return {
    recipes,
    isLoading,
    error,
    refetch,
    clearError: useCallback(() => setError(null), [])
  }
}

/**
 * Hook for recipe CRUD operations
 *
 * Provides methods for creating, updating recipes and managing ingredients
 *
 * @returns {Object} - CRUD methods and state
 */
export function useRecipeOperations() {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Create a new recipe
   *
   * @param {Object} recipeData - Recipe data
   * @returns {Promise<Object>} - Created recipe
   */
  const createRecipe = useCallback(async (recipeData) => {
    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('create', recipeData)
      return result.data
    } catch (err) {
      console.error('Error creating recipe:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Create a new version of a recipe
   * Used when ingredients change significantly
   *
   * @param {string} recipeId - Current recipe ID
   * @param {Object} updates - Optional updates (name, notes, ingredients, etc.)
   * @returns {Promise<Object>} - New version data
   */
  const createVersion = useCallback(async (recipeId, updates = {}) => {
    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('create-version', {
        id: recipeId,
        ...updates
      })
      return result.data
    } catch (err) {
      console.error('Error creating version:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Update an existing recipe
   *
   * @param {string} id - Recipe ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated recipe
   */
  const updateRecipe = useCallback(async (id, updates) => {
    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('update', { id, ...updates })
      return result.data
    } catch (err) {
      console.error('Error updating recipe:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Add ingredient to recipe
   *
   * @param {string} recipeId - Recipe ID
   * @param {Object} ingredient - Ingredient data
   * @returns {Promise<Object>} - Created ingredient
   */
  const addIngredient = useCallback(async (recipeId, ingredient) => {
    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('add-ingredient', {
        recipe_id: recipeId,
        ...ingredient
      })
      return result.data
    } catch (err) {
      console.error('Error adding ingredient:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Update an ingredient
   *
   * @param {string} ingredientId - Ingredient ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} - Updated ingredient
   */
  const updateIngredient = useCallback(async (ingredientId, updates) => {
    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('update-ingredient', {
        id: ingredientId,
        ...updates
      })
      return result.data
    } catch (err) {
      console.error('Error updating ingredient:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Delete an ingredient
   *
   * @param {string} ingredientId - Ingredient ID
   * @returns {Promise<void>}
   */
  const deleteIngredient = useCallback(async (ingredientId) => {
    try {
      setIsLoading(true)
      setError(null)

      await callRecipesFunction('delete-ingredient', { id: ingredientId })
    } catch (err) {
      console.error('Error deleting ingredient:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  return {
    // State
    isLoading,
    error,

    // Recipe operations
    createRecipe,
    updateRecipe,
    createVersion,

    // Ingredient operations
    addIngredient,
    updateIngredient,
    deleteIngredient,

    // Utility
    clearError: useCallback(() => setError(null), [])
  }
}

/**
 * Hook for fetching recipe version history
 *
 * @param {string} recipeId - Recipe ID (any version)
 * @returns {Object} - Version history data and methods
 */
export function useRecipeVersionHistory(recipeId = null) {
  const [versionHistory, setVersionHistory] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  /**
   * Fetch version history for a recipe
   *
   * @param {string} id - Recipe ID
   * @returns {Promise<Object>} - Version history data
   */
  const getVersionHistory = useCallback(async (id) => {
    if (!id) {
      setError('Recipe ID is required')
      return null
    }

    try {
      setIsLoading(true)
      setError(null)

      const result = await callRecipesFunction('version-history', { id })
      setVersionHistory(result.data)
      return result.data
    } catch (err) {
      console.error('Error fetching version history:', err)
      setError(err.message)
      throw err
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Refetch version history
   */
  const refetch = useCallback(() => {
    if (recipeId) {
      return getVersionHistory(recipeId)
    }
    return null
  }, [recipeId, getVersionHistory])

  /**
   * Clear error state
   */
  const clearError = useCallback(() => setError(null), [])

  // Auto-fetch on mount if recipeId provided
  useEffect(() => {
    if (recipeId) {
      getVersionHistory(recipeId)
    }
  }, [recipeId, getVersionHistory])

  return {
    // State
    versionHistory,
    versions: versionHistory?.versions || [],
    currentVersion: versionHistory?.current_version || null,
    totalVersions: versionHistory?.total_versions || 0,
    rootRecipeId: versionHistory?.root_recipe_id || null,
    isLoading,
    error,

    // Methods
    getVersionHistory,
    refetch,
    clearError
  }
}

export default useRecipeCost
